// src/lib/advertisingApi.ts
//
// Typed calls for the producer advertising admin (B2): the order list, one order with its
// events, create, each step, and the invoice print payload. The api is
// Controllers/Admin/AdPlacementsAdminController.cs (route api/admin/advertising, Admin only).
//
// Also the rules the admin page uses to enable each step button. They mirror the api's own
// checks so a button is never live for a step the api would refuse; the api stays the
// authority and refuses with a code either way.
//
// No price lives here or anywhere in the ui: he types the price on each order at Invoice.

import { adminFetch } from '@/lib/adminFetch';

const BASE = '/api/proxy/api/admin/advertising';

// ---- Products and statuses (the api's closed sets) ----

/** The products the order form offers. digest_item stays hidden until B8 serves it. */
export const FORM_PRODUCTS = ['featured_call'] as const;
export type FormProduct = (typeof FORM_PRODUCTS)[number];

export const PRODUCT_LABELS: Record<string, string> = {
  featured_call: 'Featured casting call',
  digest_item: 'Digest item',
};

export const AD_STATUSES = ['draft', 'buyer_checked', 'invoiced', 'paid', 'approved', 'voided', 'refunded'] as const;
export type AdStatus = (typeof AD_STATUSES)[number];

export const STATUS_LABELS: Record<AdStatus, string> = {
  draft: 'Draft',
  buyer_checked: 'Buyer checked',
  invoiced: 'Invoiced',
  paid: 'Paid',
  approved: 'Approved',
  voided: 'Voided',
  refunded: 'Refunded',
};

export const MAX_BUSINESS_NAME = 200;
export const MAX_BILLING_EMAIL = 254;
export const MAX_BANK_REFERENCE = 100;
export const MAX_NOTE = 500;
const MAX_CENTS = 2147483647;

// ---- Shapes (camelCase, as the api serializes) ----

export type AdOrder = {
  id: number;
  /** JS- and the invoice sequence; null until Invoice. */
  invoiceNumber: string | null;
  product: string;
  status: AdStatus;
  advertiserPeopleId: number;
  postId: number | null;
  metroId: number;
  businessName: string;
  /** Only on the single-order read; null in the list. */
  billingEmail: string | null;
  startsAt: string;
  endsAt: string;
  buyerCheckedAt: string | null;
  buyerCheckedByUserId: string | null;
  buyerIsAgency: boolean | null;
  stateRegistryChecked: boolean | null;
  agencyLicenseChecked: boolean | null;
  ruleBCheckedByHand: boolean | null;
  buyerCheckNote: string | null;
  priceCents: number | null;
  invoicedAt: string | null;
  amountReceivedCents: number | null;
  receivedAt: string | null;
  bankReference: string | null;
  approvedAt: string | null;
  approvedByUserId: string | null;
  viewCountAtApprove: number | null;
  createdAt: string;
  createdByUserId: string;
  updatedAt: string;
  rowVersion: string | null;
  /**
   * Approved at no charge during the beta (the beta_no_charge step): no invoice number, no price,
   * nothing received. Absent on an api that predates the step.
   */
  noCharge?: boolean;
};

export type AdEvent = {
  id: number;
  kind: string;
  amountCents: number | null;
  note: string | null;
  actorUserId: string | null;
  at: string;
};

export type AdOrderList = { total: number; skip: number; take: number; items: AdOrder[] };

export type AdOrderDetail = { order: AdOrder; events: AdEvent[] };

export type AdInvoice = {
  invoiceNumber: string;
  invoicedAt: string;
  sellerName: string;
  sellerAddress: string;
  billTo: string;
  product: string;
  productName: string;
  metroId: number;
  hubCity: string | null;
  startsAt: string;
  endsAt: string;
  dueBefore: string;
  priceCents: number;
  currency: string;
  status: AdStatus;
};

export type CreateOrderInput = {
  product: FormProduct;
  advertiserPeopleId: number;
  postId: number;
  metroId?: number | null;
  businessName: string;
  billingEmail: string;
  startsAt: string;
  endsAt: string;
};

export type BuyerCheckInput = {
  buyerIsAgency: boolean;
  stateRegistryChecked: boolean;
  agencyLicenseChecked: boolean;
  ruleBCheckedByHand: boolean;
  note: string;
};

export type ListFilters = { status?: string; product?: string; metroId?: string };

/** GET api/admin/advertising/settings: whether the beta no-charge step is on (Beta:NoCharge). */
export type AdSettings = { betaNoCharge: boolean };

/** The label for an order approved at no charge during the beta. */
export const NO_CHARGE_LABEL = 'No charge (beta)';

/** A refused call: the api's code (cap_reached, not_paid, stale_order and the rest). */
export class AdvertisingError extends Error {
  code: string;
  status: number | undefined;
  constructor(code: string, status?: number) {
    super(code);
    this.name = 'AdvertisingError';
    this.code = code;
    this.status = status;
  }
}

async function call<T>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  try {
    const res = await adminFetch<T>(path, { method: opts.method, body: opts.body, suppressToast: true });
    return res as T;
  } catch (e: any) {
    // adminFetch puts the api's { error } code in problem.title.
    const code = String(e?.problem?.title || e?.message || 'request_failed');
    throw new AdvertisingError(code, e?.status);
  }
}

// ---- Calls ----

export function listOrders(filters: ListFilters = {}): Promise<AdOrderList> {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.product) qs.set('product', filters.product);
  if (filters.metroId && /^\d+$/.test(filters.metroId)) qs.set('metroId', filters.metroId);
  qs.set('take', '500');
  return call<AdOrderList>(`${BASE}?${qs.toString()}`);
}

export function getOrder(id: number): Promise<AdOrderDetail> {
  return call<AdOrderDetail>(`${BASE}/${id}`);
}

export function createOrder(input: CreateOrderInput): Promise<AdOrder> {
  return call<AdOrder>(BASE, { method: 'POST', body: input });
}

export function recordBuyerCheck(id: number, input: BuyerCheckInput, rowVersion: string | null): Promise<AdOrder> {
  return call<AdOrder>(`${BASE}/${id}/buyer-check`, { method: 'POST', body: { ...input, rowVersion } });
}

export function invoiceOrder(id: number, priceCents: number, rowVersion: string | null): Promise<AdOrder> {
  return call<AdOrder>(`${BASE}/${id}/invoice`, { method: 'POST', body: { priceCents, rowVersion } });
}

export function markPaid(
  id: number,
  input: { amountReceivedCents: number; receivedAt: string; bankReference: string },
  rowVersion: string | null,
): Promise<AdOrder> {
  return call<AdOrder>(`${BASE}/${id}/paid`, { method: 'POST', body: { ...input, rowVersion } });
}

export function approveOrder(id: number, rowVersion: string | null): Promise<AdOrder> {
  return call<AdOrder>(`${BASE}/${id}/approve`, { method: 'POST', body: { rowVersion } });
}

export function voidOrder(id: number, reason: string, rowVersion: string | null): Promise<AdOrder> {
  return call<AdOrder>(`${BASE}/${id}/void`, { method: 'POST', body: { reason, rowVersion } });
}

export function refundOrder(id: number, amountCents: number, reason: string, rowVersion: string | null): Promise<AdOrder> {
  return call<AdOrder>(`${BASE}/${id}/refund`, { method: 'POST', body: { amountCents, reason, rowVersion } });
}

export function getInvoice(id: number): Promise<AdInvoice> {
  return call<AdInvoice>(`${BASE}/${id}/invoice`);
}

/** Whether the beta no-charge step is on. Any failure reads as off, so the button stays hidden. */
export async function getSettings(): Promise<AdSettings> {
  try {
    const s = await call<AdSettings | undefined>(`${BASE}/settings`);
    return { betaNoCharge: (s as any)?.betaNoCharge === true };
  } catch {
    return { betaNoCharge: false };
  }
}

/**
 * Approve at no charge (beta): buyer checked to approved with no invoice, no price and nothing
 * received. The api refuses with beta_off when Beta:NoCharge is false, and with the same checks
 * Approve makes (cap_reached and the rest).
 */
export function approveAtNoCharge(id: number, rowVersion: string | null): Promise<AdOrder> {
  return call<AdOrder>(`${BASE}/${id}/beta-no-charge`, { method: 'POST', body: { rowVersion } });
}

// ---- Dates (the api's datetime2 columns come back without a zone; they are UTC) ----

/** Epoch ms for an api timestamp, reading a zone-less value as UTC. NaN when unreadable. */
export function parseApiDate(value: string | null | undefined): number {
  if (!value) return NaN;
  const s = String(value);
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(s);
  return Date.parse(hasZone ? s : s + 'Z');
}

const DATE_FMT = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric' });
const DATE_TIME_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});

export function formatUtcDate(value: string | null | undefined): string {
  const t = parseApiDate(value);
  return Number.isFinite(t) ? DATE_FMT.format(t) : '';
}

export function formatUtcDateTime(value: string | null | undefined): string {
  const t = parseApiDate(value);
  return Number.isFinite(t) ? DATE_TIME_FMT.format(t) + ' UTC' : '';
}

/** A yyyy-mm-dd date input as that day's 00:00 UTC, or null. */
export function dateInputToUtcIso(day: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const t = Date.parse(day + 'T00:00:00Z');
  return Number.isFinite(t) ? new Date(t).toISOString() : null;
}

// ---- Money (always Intl; never a currency sign written in source) ----

export function formatCents(cents: number | null | undefined, currency = 'USD'): string {
  if (cents == null || !Number.isFinite(cents)) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
}

/** A typed amount in dollars ("12", "12.5", "12.50") as whole cents above 0, or null. */
export function parseAmountToCents(text: string): number | null {
  const s = (text || '').trim();
  const m = /^(\d{1,8})(?:\.(\d{1,2}))?$/.exec(s);
  if (!m) return null;
  const cents = Number(m[1]) * 100 + Number((m[2] || '').padEnd(2, '0') || '0');
  if (!Number.isInteger(cents) || cents <= 0 || cents > MAX_CENTS) return null;
  return cents;
}

// ---- Which steps are legal now (mirrors AdPlacementService) ----

export type StepKey = 'buyerCheck' | 'invoice' | 'paid' | 'approve' | 'void' | 'refund';

export function legalSteps(order: Pick<AdOrder, 'status' | 'startsAt' | 'endsAt'>, nowMs: number = Date.now()): Record<StepKey, boolean> {
  const s = order.status;
  const startsInFuture = parseApiDate(order.startsAt) > nowMs;
  const notEnded = parseApiDate(order.endsAt) > nowMs;
  return {
    buyerCheck: s === 'draft',
    invoice: s === 'buyer_checked' && startsInFuture,
    paid: s === 'invoiced',
    approve: s === 'paid' && notEnded,
    void: s === 'draft' || s === 'buyer_checked' || s === 'invoiced',
    refund: s === 'paid' || s === 'approved',
  };
}

/** The beta no-charge step is legal: buyer checked and the window not ended (as Approve). */
export function betaNoChargeLegal(order: Pick<AdOrder, 'status' | 'endsAt'>, nowMs: number = Date.now()): boolean {
  return order.status === 'buyer_checked' && parseApiDate(order.endsAt) > nowMs;
}

/**
 * Void stops an approved order that was approved at no charge (beta). Refund cannot: nothing
 * was received, so the api refuses any refund amount. Needs the api's Void to take an approved
 * order whose noCharge is true; until it does, the api answers paid_use_refund.
 */
export function noChargeVoidLegal(order: Pick<AdOrder, 'status' | 'noCharge'>): boolean {
  return order.status === 'approved' && order.noCharge === true;
}

export type BuyerCheckForm = {
  buyerIsAgency: boolean | null;
  stateRegistryChecked: boolean;
  agencyLicenseChecked: boolean;
  ruleBCheckedByHand: boolean;
  note: string;
};

/** The buyer check is complete: the buyer type, the registry, a license for an agency, the rule B check and a note. */
export function buyerCheckComplete(f: BuyerCheckForm): boolean {
  if (f.buyerIsAgency == null) return false;
  if (!f.stateRegistryChecked) return false;
  if (f.buyerIsAgency && !f.agencyLicenseChecked) return false;
  if (!f.ruleBCheckedByHand) return false;
  const note = f.note.trim();
  return note.length > 0 && note.length <= MAX_NOTE;
}

/** Mark paid is ready: an amount above 0 and not above the invoiced price, a date from the order's day to today, a reference. */
export function paidReady(
  order: Pick<AdOrder, 'priceCents' | 'createdAt'>,
  f: { amount: string; receivedOn: string; bankReference: string },
  nowMs: number = Date.now(),
): boolean {
  const cents = parseAmountToCents(f.amount);
  if (cents == null) return false;
  if (order.priceCents != null && cents > order.priceCents) return false;
  const iso = dateInputToUtcIso(f.receivedOn);
  if (!iso) return false;
  const t = Date.parse(iso);
  if (t > nowMs) return false;
  const created = parseApiDate(order.createdAt);
  if (Number.isFinite(created)) {
    const d = new Date(created);
    if (t < Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())) return false;
  }
  const ref = f.bankReference.trim();
  return ref.length > 0 && ref.length <= MAX_BANK_REFERENCE;
}

export function reasonReady(reason: string): boolean {
  const r = reason.trim();
  return r.length > 0 && r.length <= MAX_NOTE;
}

/** Refund is ready: an amount above 0 and not above what was received, and a reason. */
export function refundReady(order: Pick<AdOrder, 'amountReceivedCents'>, f: { amount: string; reason: string }): boolean {
  const cents = parseAmountToCents(f.amount);
  if (cents == null || cents > (order.amountReceivedCents ?? 0)) return false;
  return reasonReady(f.reason);
}

export type OrderForm = {
  advertiserPeopleId: string;
  postId: string;
  metroId: string;
  businessName: string;
  billingEmail: string;
  startsOn: string;
  endsOn: string;
};

/** The order form as a create body, or null while a field is missing or out of range. */
export function orderFormToInput(f: OrderForm): CreateOrderInput | null {
  const positive = (s: string) => (/^\d+$/.test(s.trim()) && Number(s) > 0 ? Number(s) : null);
  const advertiserPeopleId = positive(f.advertiserPeopleId);
  const postId = positive(f.postId);
  if (advertiserPeopleId == null || postId == null) return null;
  let metroId: number | null = null;
  if (f.metroId.trim() !== '') {
    metroId = positive(f.metroId);
    if (metroId == null) return null;
  }
  const businessName = f.businessName.trim();
  if (businessName.length === 0 || businessName.length > MAX_BUSINESS_NAME) return null;
  const billingEmail = f.billingEmail.trim();
  if (billingEmail.length > MAX_BILLING_EMAIL || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billingEmail)) return null;
  const startsAt = dateInputToUtcIso(f.startsOn);
  const endsAt = dateInputToUtcIso(f.endsOn);
  if (!startsAt || !endsAt || Date.parse(endsAt) <= Date.parse(startsAt)) return null;
  return { product: 'featured_call', advertiserPeopleId, postId, metroId, businessName, billingEmail, startsAt, endsAt };
}
